/**
 * The principal risks and uncertainties, and how each is managed.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Nothing outside this folder needs to
 * know the kind exists.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **Why this is a kind of its own rather than a table or an IRO register.**
 *
 * A French annual financial report has to describe "les principaux risques et
 * incertitudes": the responsible officers attest to it in so many words, and a
 * report that omits the description is one they cannot sign. Every such report
 * sets it out the same way, grouped by family of risk, each risk with what it
 * is and what is done about it. That shape is not a table: the description and
 * the controls are paragraphs, and squeezing them into cells is how a risk
 * section ends up unreadable.
 *
 * Nor is it the IRO register. An IRO is a sustainability matter with columns
 * ESRS fixes, the place in the value chain and the time horizon. A risk factor
 * is the company's own exposure and its controls, and it would be wrong in an
 * IRO register as surely as an IRO would be wrong here.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { readArray, readString } from '@/domain/json'
import type { JsonValue } from '@/domain/json'
import { newId, newRiskEntry, type RiskEntry, type RisksSection } from '@/domain/section'
import RisksBlock from '@/sections/risks/RisksBlock.vue'
import RisksInspector from '@/sections/risks/RisksInspector.vue'
import type { SectionDefinition } from '@/sections/types'

/**
 * How severe a risk is judged, once the controls in place are allowed for.
 *
 * Three and no more. The regulator asks for the most significant risks to be
 * presented first and to be recognisable as such; a five-point scale invites
 * a debate about whether something is a three or a four, which is not a
 * debate the reader of the report benefits from.
 */
export const RISK_LEVELS = ['high', 'medium', 'low'] as const

function readLevel(value: string): RiskEntry['level'] {
  return (RISK_LEVELS as readonly string[]).includes(value)
    ? (value as RiskEntry['level'])
    : 'medium'
}

function readEntry(value: JsonValue): RiskEntry {
  return {
    id: readString(value, 'id') || newId(),
    category: readString(value, 'category'),
    title: readString(value, 'title'),
    level: readLevel(readString(value, 'level')),
    description: readString(value, 'description'),
    management: readString(value, 'management'),
  }
}

export const risksSection: SectionDefinition<'risks', RisksSection> = {
  kind: 'risks',
  band: 'content',

  icon: ['M8 2.5l5.5 10h-11z', 'M8 6.5v3', 'M8 11.2v.3'],

  create: (id, title) => ({
    id,
    kind: 'risks',
    title,
    entries: [newRiskEntry()],
    caption: '',
  }),

  read: ({ id, title, stored }) => ({
    id,
    kind: 'risks',
    title,
    entries: readArray(stored, 'entries').map(readEntry),
    caption: readString(stored, 'caption'),
  }),

  block: RisksBlock,
  inspector: RisksInspector,
}
